import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_MY_METRIC_PERMISSIONS } from 'src/utils/gql/queries/userMetricPermission';
import { ConnectorType, UserMetricPermission } from 'src/utils/gql/types';

export const useMyMetricPermissions = () => {
  const { data, loading, error } = useQuery(GET_MY_METRIC_PERMISSIONS);

  const permissions = data?.getMyMetricPermissions || [];

  // Organizza i permessi per connettore e metrica
  const permissionsMap = useMemo(() => {
    const map: Record<ConnectorType, Record<string, boolean>> = {
      [ConnectorType.SHOPIFY]: {},
      [ConnectorType.FACEBOOK]: {},
      [ConnectorType.GOOGLE_ADS]: {},
      [ConnectorType.GOOGlE_ANALYTICS4]: {},
      [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {},
      [ConnectorType.KIPIFY]: {},
      [ConnectorType.DATABASE]: {},
      [ConnectorType.MAGENTO]: {},
    };

    permissions.forEach((permission: UserMetricPermission) => {
      if (map[permission.connectorType]) {
        map[permission.connectorType][permission.metricKey] =
          permission.isEnabled;
      }
    });

    return map;
  }, [permissions]);

  // Function to check if a metric is enabled for the user
  const isMetricEnabled = (
    connectorType: ConnectorType,
    metricKey: string,
  ): boolean => {
    // Se non ci sono permessi specifici, la metrica è abilitata di default
    if (!permissions.length) {
      return true;
    }

    const permission = permissionsMap[connectorType]?.[metricKey];
    return permission !== undefined ? permission : true; // Default enabled
  };

  // Funzione per filtrare le metriche in base ai permessi
  const filterMetricsByPermissions = <
    T extends { key: string; connectorType?: ConnectorType },
  >(
    metrics: T[],
    connectorType: ConnectorType,
  ): T[] => {
    return metrics.filter((metric) => {
      const metricKey = metric.key || metric.connectorType;
      return metricKey ? isMetricEnabled(connectorType, metricKey) : true;
    });
  };

  return {
    permissions,
    permissionsMap,
    isMetricEnabled,
    filterMetricsByPermissions,
    loading,
    error,
  };
};
